import { getDocs, collection, doc } from "firebase/firestore";
import { db } from '../../../../../firebase/config';
import { fetchCompaniesData } from '../../../../companies/service/companiesService';
import type { Company } from '../../../../companies/service/companiesService';
import {
    fetchDeploymentBankId,
    fetchBranches,
    createDeployment,
    checkDeploymentConflict,
    type Branch,
} from '../../../service/deploymentsService';

/** One deployment row on the branch, with Firestore document id for updates/removals */
export type DeployedCompanyEntry = {
    deploymentId: string;
    company: Company;
};

export type DeploymentFormData = {
    branchId: string;
    companiesId: string;
};

/**
 * Fetch available branches for the current user
 */
export async function fetchAvailableBranches(uid: string, isBankUser: boolean = false): Promise<Branch[]> {
    const bankId = await fetchDeploymentBankId(uid, isBankUser);
    if (!bankId) {
        return [];
    }
    return await fetchBranches(bankId, isBankUser ? undefined : uid);
}

/**
 * Fetch available companies for the current user
 */
export async function fetchAvailableCompanies(uid: string, isBankUser: boolean = false): Promise<Company[]> {
    return await fetchCompaniesData(uid, isBankUser);
}

/**
 * Fetch deployed companies for a specific branch
 */
export async function fetchDeployedCompaniesForBranch(
    uid: string,
    isBankUser: boolean,
    branchId: string
): Promise<DeployedCompanyEntry[]> {
    try {
        const bankId = await fetchDeploymentBankId(uid, isBankUser);
        if (!bankId) {
            return [];
        }

        // Fetch all deployments for this branch
        const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
        const deployments = deploymentsSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter((d: any) => d.branchId === branchId);

        // Fetch all companies
        const companies = await fetchCompaniesData(uid, isBankUser);

        // Map deployments to companies (preserves multiple rows per type, e.g. BAMS)
        const deployedCompanies: DeployedCompanyEntry[] = [];
        for (const deployment of deployments) {
            const company = companies.find(c => c.id === (deployment as any).companiesId);
            if (company) {
                deployedCompanies.push({ deploymentId: deployment.id, company });
            }
        }

        return deployedCompanies;
    } catch (error) {
        console.error('Error fetching deployed companies for branch:', error);
        return [];
    }
}

/**
 * Submit a new deployment
 */
export async function submitDeployment(
    uid: string,
    isBankUser: boolean,
    branchId: string,
    companiesId: string
): Promise<{ success: boolean; conflict?: { existingDeployment: any; existingCompany: Company } }> {
    try {
        const bankId = await fetchDeploymentBankId(uid, isBankUser);
        if (!bankId) {
            throw new Error('Bank ID not found');
        }

        // Check for conflicts
        const conflictCheck = await checkDeploymentConflict(bankId, branchId, companiesId);
        if (conflictCheck.hasConflict) {
            return {
                success: false,
                conflict: {
                    existingDeployment: conflictCheck.existingDeployment,
                    existingCompany: conflictCheck.existingCompany!
                }
            };
        }

        // Create deployment
        await createDeployment(bankId, branchId, companiesId, uid);
        return { success: true };
    } catch (error) {
        console.error('Error submitting deployment:', error);
        throw error;
    }
}

/**
 * Replace existing deployment (used when resolving conflicts)
 */
export async function replaceDeployment(
    uid: string,
    isBankUser: boolean,
    branchId: string,
    companiesId: string,
    existingDeploymentId: string
): Promise<void> {
    try {
        const bankId = await fetchDeploymentBankId(uid, isBankUser);
        if (!bankId) {
            throw new Error('Bank ID not found');
        }

        // Delete existing deployment
        const { deleteDoc } = await import('firebase/firestore');
        const existingDeploymentRef = doc(db, "banks", bankId, "companyDeployment", existingDeploymentId);
        await deleteDoc(existingDeploymentRef);

        // Create new deployment
        await createDeployment(bankId, branchId, companiesId, uid);
    } catch (error) {
        console.error('Error replacing deployment:', error);
        throw error;
    }
}

/**
 * Process bulk deployments from Excel data
 */
export async function processBulkDeployments(
    uid: string,
    isBankUser: boolean,
    excelData: any[],
    availableBranches: Branch[],
    availableCompanies: Company[]
): Promise<{ results: any[]; undoData: any[] }> {
    const results: any[] = [];
    const undoData: any[] = [];
    const bankId = await fetchDeploymentBankId(uid, isBankUser);
    
    if (!bankId) {
        return {
            results: excelData.map((row, index) => ({
                rowNumber: index + 2, // +2 because Excel rows start at 1 and we skip header
                branchId: row.branchId || '',
                companyName: row.companyName || '',
                status: 'error',
                message: 'Bank ID not found'
            })),
            undoData: []
        };
    }

    for (let index = 0; index < excelData.length; index++) {
        const row = excelData[index];
        const result: any = {
            rowNumber: index + 2, // +2 because Excel rows start at 1 and we skip header
            branchId: row.branchId || row.branchid || '',
            companyName: row.companyName || row.companyname || '',
            status: 'pending',
            message: ''
        };

        try {
            // Validate required fields
            if (!result.branchId) {
                throw new Error('Branch ID is required');
            }
            if (!result.companyName) {
                throw new Error('Company Name is required');
            }

            // Find branch by branchCode (Excel sends branchCode as branchId)
            const branch = availableBranches.find(b => 
                String(b.branchCode || '').toLowerCase() === String(result.branchId).toLowerCase() ||
                b.id === result.branchId || 
                String(b.id).toLowerCase() === String(result.branchId).toLowerCase()
            );

            if (!branch) {
                throw new Error(`Branch with code "${result.branchId}" not found`);
            }

            // Find company by name (case-insensitive) in available companies first
            let company = availableCompanies.find(c => 
                String(c.companyName || '').toLowerCase().trim() === String(result.companyName).toLowerCase().trim()
            );

            // If not found in available companies, search in global /companies collection
            const firestoreModule = await import('firebase/firestore');
            const { getDocs, collection, updateDoc, doc } = firestoreModule;
            const { db } = await import('../../../../../firebase/config');

            if (!company) {
                // Fetch all companies from global collection
                const globalCompaniesSnap = await getDocs(collection(db, "companies"));
                const globalCompany = globalCompaniesSnap.docs
                    .map(doc => ({ id: doc.id, ...doc.data() } as any))
                    .find((c: any) => 
                        String(c.companyName || '').toLowerCase().trim() === String(result.companyName).toLowerCase().trim()
                    );

                if (globalCompany) {
                    // Use the global company's ID (document ID or userId)
                    const companyId = globalCompany.id || globalCompany.userId;
                    
                    // Check if this company exists in bank's companies collection
                    const bankCompaniesSnap = await getDocs(collection(db, "banks", bankId, "companies"));
                    const bankCompanyDoc = bankCompaniesSnap.docs.find(d => {
                        const data = d.data();
                        return d.id === companyId || data.companyId === companyId;
                    });

                    if (bankCompanyDoc) {
                        // Company exists in bank's collection, use it
                        const bankCompanyData = bankCompanyDoc.data();
                        company = {
                            id: bankCompanyDoc.id,
                            bankId: bankId,
                            companyId: companyId,
                            companyName: globalCompany.companyName,
                            companyType: bankCompanyData.companyType || globalCompany.companyType,
                            companyNo: globalCompany.companyNo,
                            email: globalCompany.email,
                            companyAddress: globalCompany.companyAddress,
                            companyImg: globalCompany.imgUrl || globalCompany.companyImg,
                            contactPersonName: bankCompanyData.contactPersonName,
                            contactMobileNumber: bankCompanyData.contactMobileNumber,
                            contactEmail: bankCompanyData.contactEmail,
                            status: bankCompanyData.status
                        } as Company;
                    } else {
                        // Company not in bank's collection - must be added first
                        throw new Error(`Company "${result.companyName}" is not added to your bank's vendor list. Please add it first from Manage Vendors.`);
                    }
                } else {
                    throw new Error(`Company with name "${result.companyName}" not found in global companies`);
                }
            }

            // Ensure company is defined
            if (!company) {
                throw new Error(`Company with name "${result.companyName}" not found`);
            }

            // TypeScript: company is guaranteed to be defined after the check above
            const finalCompany = company;

            // Check if this company is already deployed to this branch
            const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
            const existingDeployment = deploymentsSnap.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as any))
                .find((d: any) => d.branchId === branch.id && d.companiesId === finalCompany.id);

            if (existingDeployment) {
                // Store undo data before updating
                undoData.push({
                    deploymentId: existingDeployment.id,
                    previousCompanyId: existingDeployment.companiesId,
                    newCompanyId: finalCompany.id,
                    branchId: branch.id,
                    branchCode: branch.branchCode
                });

                // Update existing deployment
                const deploymentRef = doc(db, "banks", bankId, "companyDeployment", existingDeployment.id);
                await updateDoc(deploymentRef, {
                    companiesId: finalCompany.id,
                    updatedAt: new Date()
                });
                result.status = 'updated';
                result.message = `Updated deployment: ${finalCompany.companyName} to branch ${branch.branchCode || branch.id}`;
                result.deploymentId = existingDeployment.id;
                result.previousCompanyId = existingDeployment.companiesId;
            } else {
                // Check for conflicts (same company type)
                const conflictCheck = await checkDeploymentConflict(bankId, branch.id, finalCompany.id);
                if (conflictCheck.hasConflict) {
                    result.status = 'skip';
                    result.message = `Branch already has ${conflictCheck.existingCompany?.companyType} company: ${conflictCheck.existingCompany?.companyName}`;
                } else {
                    // Create new deployment
                    const deploymentId = await createDeployment(bankId, branch.id, finalCompany.id, uid);
                    result.status = 'success';
                    result.message = `Successfully deployed ${finalCompany.companyName} to branch ${branch.branchCode || branch.id}`;
                    result.deploymentId = deploymentId;
                }
            }
        } catch (error: any) {
            result.status = 'error';
            result.message = error.message || 'Failed to process deployment';
        }

        results.push(result);
    }

    return { results, undoData };
}

